import { EmailTemplate, MailConfigState } from "@/types";
import { Module } from "vuex";
import Notify from 'quasar/src/plugins/Notify.js';;
import i18n from "@/i18n";
import { logger } from "@/utils/logger";
import { handlingError } from "@/utils";
import {
  cloneMailAsNew,
  createMailTemplate,
  getEmptyMailTemplate,
  getMailTemplate,
  mapToEmailTemplate,
  queryMailTemplates,
  toMailEditorHtml,
  updateMailTemplate,
} from "@/store/services/mail/mailConfigService";

const mailConfigModule: Module<MailConfigState, any> = {
  namespaced: true,
  state: {
    mode: "add",
    selectedMail: {},
    duplicatedFrom: null,
  },
  getters: {
    getMailInit() {
      return getEmptyMailTemplate();
    },
  },
  actions: {
    searchMailConfig(_context, payload) {
      const { filter, offset, limit, callback } = payload;
      return queryMailTemplates(filter, offset, limit)
        .then((response) => {
          callback(response);
        })
        .catch((err) => {
          logger.error(err);
          throw err;
        });
    },
    setMode({ commit }, payload) {
      commit("setMode", payload);
    },
    initMailConfig({ commit, getters }) {
      commit("setDuplicatedFrom", null);
      commit("initMailConfig", getters.getMailInit);
    },
    executeSelectMail({ commit }, payload) {
      const { mail, callback } = payload;
      commit("setDuplicatedFrom", null);
      const fromList = mapToEmailTemplate(mail);
      const resourceUid = fromList.resourceUid;
      if (!resourceUid) {
        commit("setSelectedMail", getEmptyMailTemplate());
        callback?.();
        return Promise.resolve();
      }
      return getMailTemplate(resourceUid)
        .then((selected) => {
          const next = selected || getEmptyMailTemplate();
          next.sender = next.sender || fromList.sender;
          next.recipient = next.recipient || fromList.recipient;
          next.copyRecipient = next.copyRecipient || fromList.copyRecipient || "";
          next.hiddenRecipient = next.hiddenRecipient || fromList.hiddenRecipient || "";
          next.subject = next.subject || fromList.subject;
          next.body = next.body || toMailEditorHtml(fromList.body);
          commit("setSelectedMail", next);
          callback?.();
        })
        .catch((err) => {
          if (fromList.subject || fromList.body) {
            fromList.copyRecipient = fromList.copyRecipient || "";
            fromList.hiddenRecipient = fromList.hiddenRecipient || "";
            fromList.body = toMailEditorHtml(fromList.body);
            commit("setSelectedMail", fromList);
            callback?.();
            return;
          }
          handlingError(err);
          throw err;
        });
    },
    duplicateMail({ commit }, payload) {
      const { mail, callback } = payload;
      const fromList = mapToEmailTemplate(mail);
      const resourceUid = fromList.resourceUid;
      const applyCopy = (source: any) => {
        const copy = cloneMailAsNew(source);
        commit("setMode", "add");
        commit("setDuplicatedFrom", resourceUid || null);
        commit("setSelectedMail", copy);
        callback?.();
      };
      if (!resourceUid) {
        applyCopy(fromList);
        return Promise.resolve();
      }
      return getMailTemplate(resourceUid)
        .then((selected) => {
          applyCopy({
            ...fromList,
            ...selected,
            body: selected?.body || fromList.body,
            subject: selected?.subject || fromList.subject,
          });
        })
        .catch(() => {
          applyCopy(fromList);
        });
    },
    saveMailConfig({ state, commit }, payload) {
      const { mail, callback } = payload;
      const mode = state.mode || "edit";
      const isDuplicate = mode === "add" && !!state.duplicatedFrom;
      const persist =
        mode === "add"
          ? createMailTemplate(mail as EmailTemplate)
          : updateMailTemplate(mail as EmailTemplate);

      return persist
        .then((saved) => {
          commit("setSelectedMail", saved);
          commit("setDuplicatedFrom", null);
          Notify.create({
            timeout: 3000,
            actions: [{ icon: "close", color: "white" }],
            message: isDuplicate
              ? i18n.global.t("mailConfig.dialog.Duplicatesuccess")
              : mode === "add"
                ? i18n.global.t("mailConfig.dialog.Addsuccess")
                : i18n.global.t("mailConfig.dialog.Editsuccess"),
            color: "positive",
          });
          callback?.();
        })
        .catch((err) => {
          handlingError(err);
          throw err;
        });
    },
  },
  mutations: {
    setMode(state, payload) {
      state.mode = payload;
    },
    initMailConfig(state, payload) {
      state.selectedMail = payload;
    },
    setSelectedMail(state, payload) {
      state.selectedMail = payload;
    },
    setDuplicatedFrom(state, payload) {
      state.duplicatedFrom = payload || null;
    },
  },
};

export default mailConfigModule;
